// @ts-nocheck
import { ACCTION_QUESTION_ID, MESSAGE_TYPE_ENUM, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE } from "../constants";
import { buildAddonQuantityMap } from "./configurationPriceCalculation";
import {
  IAssistantMessage,
  IConfigBreakDown,
  IConfigurationBreakdownDetail,
  IQuestion,
} from "../types";

/**
 * Helper function to get display value from message
 * If form has only one value, returns that value; otherwise returns the message
 */
export const getDisplayValue = (message: IAssistantMessage): string => {
  const form = message.response?.form;
  const answers = message.response?.answer;

  if (form && typeof form === 'object') {
    const entries = Object.entries(form).filter(
      ([, val]) => val !== null && val !== undefined && String(val).trim() !== ''
    );

    if (entries.length > 0) {
      // Build a label map from answer options: responseValue → optionText
      const labelMap: Record<string, string> = {};
      if (Array.isArray(answers)) {
        answers.forEach((opt) => {
          if (opt.responseValue && opt.optionText) {
            labelMap[opt.responseValue] = opt.optionText;
          }
        });
      }

      return entries
        .map(([key, val]) => {
          const label = labelMap[key];
          return label ? `${label}: ${val}` : String(val);
        })
        .join(', ');
    }
  }

  // Otherwise, return the message directly
  return message.message || '';
};

export const getFirstQuestion = (questions: IQuestion[]): IQuestion | null => {
  if (questions.length === 0) return null;
  return questions[0];
};

export const getNextQuestion = (
  currentQuestionId: string,
  questions: IQuestion[],
): IQuestion | null => {
  if (questions.length === 0) return null;

  // Find the current question's index
  const currentIndex = questions.findIndex(
    (q) => q.questionId === currentQuestionId,
  );

  // If current question not found or it's the last question
  if (currentIndex === -1 || currentIndex === questions.length - 1) {
    return null;
  }

  // Return the NEXT question (current index + 1)
  return questions[currentIndex + 1];
};

export const getQuestionById = (
  questionId: string,
  questions: IQuestion[],
): IQuestion | null => {
  return questions.find((q) => q.questionId === questionId) || null;
};

export const isLastQuestion = (
  questionId: string,
  questions: IQuestion[],
): boolean => {
  if (questions.length === 0) return true;
  const lastQuestion = questions[questions.length - 1];
  return lastQuestion.questionId === questionId;
};

export const getConfigurationsBreakdown = (
  message: IAssistantMessage[],
): IConfigurationBreakdownDetail[] => {
  const userMessages = message
    .filter((msg) => msg.type === MESSAGE_TYPE_ENUM.user)
    .filter((msg) => msg.question != null)
    .filter((msg) => !ACCTION_QUESTION_ID.includes(msg.question.questionId));

  // Collect the optionIds of every "same for all windows" answer the user gave.
  // Unit = numberOfFrames only for questions whose displayCondition.dependsOn
  // directly references one of these IDs.
  const sameForAllOptionIds = new Set<string>();
  userMessages.forEach((msg) => {
    msg.response?.answer?.forEach((opt) => {
      if (opt.responseValue === 'same_for_all_windows') {
        sameForAllOptionIds.add(opt.optionId);
      }
    });
  });

  /** True when this message's question directly depends on a "same for all" answer. */
  const dependsOnSameForAll = (msg: IAssistantMessage): boolean =>
    (msg.question.displayCondition?.dependsOn ?? []).some((dep) =>
      dep.optionIds?.some((id) => sameForAllOptionIds.has(id))
    );

  // Resolve numberOfFrames — prefer variation (optionType 64), fall back to window type.
  let numberOfFrames = 1;
  if (sameForAllOptionIds.size > 0) {
    let found = false;
    for (const msg of userMessages) {
      for (const opt of msg.response?.answer ?? []) {
        if (opt.optionType === OPTION_TYPE_VARIATION && opt.numberOfFrames > 0) {
          numberOfFrames = opt.numberOfFrames;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (!found) {
      for (const msg of userMessages) {
        for (const opt of msg.response?.answer ?? []) {
          if (opt.optionType === OPTION_TYPE_WINDOW_TYPE && opt.numberOfFrames > 0) {
            numberOfFrames = opt.numberOfFrames;
            break;
          }
        }
        if (numberOfFrames > 1) break;
      }
    }
  }

  // Helper function to extract frame/window name from question text
  const extractWindowName = (questionText: string): string | undefined => {
    if (!questionText) return undefined;
    // Match "Frame N" or "Window N" (legacy) — always return as "Frame N"
    const match = questionText.match(/\b(?:frame|window)\s+(\d+)\b/i);
    return match ? `Frame ${match[1]}` : undefined;
  };

  // Pre-compute addon quantity multipliers so size rows reflect the full order quantity.
  const addonQuantityMap = buildAddonQuantityMap(userMessages);

  // Build breakdown items — one per user message
  const allBreakdowns: IConfigBreakDown[] = [];

  userMessages.forEach((msg) => {
    const windowName = extractWindowName(msg.question.question);
    const answers = msg.response?.answer ?? [];
    const form = msg.response?.form ?? {};

    // Addon quantity for Head/Cill/Jamb size questions (defaults to 1).
    const addonQty = addonQuantityMap[msg.question?.optionType] ?? 1;

    // Emit exactly one breakdown row per user message regardless of whether
    // any of its form-field options carry a price. The combined price is the
    // sum of all priced option values so the cart always shows a single
    // "Dimension" (or other category) entry. Multiply by addon quantity when set.
    const totalFormPrice = answers.reduce(
      (sum, opt) => sum + ((opt.priceModifier ?? 0) > 0 ? (opt.priceModifier ?? 0) : (opt.price ?? 0)),
      0,
    );

    const baseUnit = dependsOnSameForAll(msg) ? numberOfFrames : 1;
    const unit = baseUnit * addonQty;

    allBreakdowns.push({
      categoryName: msg.question.categoryName,
      message: msg,
      windowName,
      unit,
      price: totalFormPrice > 0 ? totalFormPrice * addonQty : undefined,
    });
  });

  // Group by categoryName
  const groupedByCategory = allBreakdowns.reduce(
    (acc, item) => {
      const category = item.categoryName || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    },
    {} as Record<string, IConfigBreakDown[]>,
  );

  // Convert to array format with category headers
  return Object.entries(groupedByCategory).map(([categoryName, details]) => ({
    categoryName,
    details,
  }));
};;
